import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LeadStatusEnum,
  callSheetPrintGaps,
  type LeadRecord,
  type LeadStatus,
} from '@flashpoint/site-dna';
import { onLeadsSnapshot, fetchLeadsOnce } from '../lib/firestore';
import { LeadListRow } from '../components/LeadListRow';
import { NewLeadDialog } from '../components/NewLeadDialog';
import { STATUS_META } from '../lib/statusMeta';

/** A demo older than this without a lock shows up in the Up-next rail. */
const STALE_DEMO_DAYS = 5;

function leadName(lead: LeadRecord): string {
  return lead.siteDna.businessInfo.name?.trim() || '(untitled)';
}

function leadIndustry(lead: LeadRecord): string {
  return lead.context.industryVertical || lead.siteDna.businessInfo.industryVertical || '';
}

function leadGapCount(lead: LeadRecord): number {
  return callSheetPrintGaps(lead.siteDna.businessInfo, leadIndustry(lead)).length;
}

function daysSince(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

interface UpNextItem {
  leadId: string;
  name: string;
  note: string;
  tone: 'gap' | 'ready' | 'stale';
}

function matchesSearch(lead: LeadRecord, q: string): boolean {
  if (!q) return true;
  const hay = [leadName(lead), leadIndustry(lead), lead.provisioning.hostname ?? '']
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export function LeadsPage() {
  const [leads, setLeads] = useState<LeadRecord[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Set<LeadStatus>>(new Set());
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setListError(null);

    const unsub = onLeadsSnapshot(
      (next) => {
        if (!cancelled) {
          setLeads(next);
          setListError(null);
        }
      },
      (msg) => {
        if (!cancelled) setListError(msg);
      },
    );

    void fetchLeadsOnce()
      .then((rows) => {
        if (!cancelled) {
          setLeads((prev) => (prev === null ? rows : prev));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setListError(err instanceof Error ? err.message : String(err));
          setLeads((prev) => (prev === null ? [] : prev));
        }
      });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const statusCounts = useMemo(() => {
    const counts: Record<LeadStatus, number> = {
      Discovery: 0,
      'Demo Generated': 0,
      'In Pipeline': 0,
    };
    for (const lead of leads ?? []) counts[lead.leadStatus] += 1;
    return counts;
  }, [leads]);

  const demosEver = statusCounts['Demo Generated'] + statusCounts['In Pipeline'];

  const upNext = useMemo<UpNextItem[]>(() => {
    if (!leads) return [];
    const gapsItems: UpNextItem[] = [];
    const readyItems: UpNextItem[] = [];
    const staleItems: UpNextItem[] = [];
    for (const lead of leads) {
      if (lead.leadStatus === 'Discovery') {
        const gaps = leadGapCount(lead);
        if (gaps > 0) {
          gapsItems.push({
            leadId: lead.id,
            name: leadName(lead),
            note: `${gaps} gap${gaps === 1 ? '' : 's'} left`,
            tone: 'gap',
          });
        } else {
          readyItems.push({
            leadId: lead.id,
            name: leadName(lead),
            note: 'ready to generate',
            tone: 'ready',
          });
        }
      } else if (lead.leadStatus === 'Demo Generated') {
        const days = daysSince(lead.provisioning.finishedAt ?? lead.updatedAt);
        if (days !== null && days >= STALE_DEMO_DAYS) {
          staleItems.push({
            leadId: lead.id,
            name: leadName(lead),
            note: `demo live ${days} days, not locked`,
            tone: 'stale',
          });
        }
      }
    }
    return [...readyItems, ...gapsItems, ...staleItems];
  }, [leads]);

  const query = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!leads) return [];
    return leads.filter(
      (l) =>
        (statusFilter.size === 0 || statusFilter.has(l.leadStatus)) && matchesSearch(l, query),
    );
  }, [leads, statusFilter, query]);

  const grouped = useMemo(() => {
    const map = new Map<LeadStatus, LeadRecord[]>();
    for (const status of LeadStatusEnum.options) map.set(status, []);
    for (const lead of filtered) map.get(lead.leadStatus)?.push(lead);
    return Array.from(map.entries()).filter(([, arr]) => arr.length > 0);
  }, [filtered]);

  const toggleStatus = (status: LeadStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const filtering = statusFilter.size > 0 || query.length > 0;

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="fp-label mb-1">Call sheet</p>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-nm-ink sm:text-3xl">
            Previews
            {leads && leads.length > 0 ? (
              <span className="ml-2 align-middle font-mono text-sm font-normal tracking-normal text-nm-tertiary">
                {leads.length}
              </span>
            ) : null}
          </h1>
          <p className="mt-1 text-sm text-nm-muted">
            Start a call sheet, generate a live Pages link, send it before you hang up.
          </p>
        </div>
        <button type="button" onClick={() => setNewOpen(true)} className="fp-btn fp-btn-primary">
          + New preview
        </button>
      </header>

      {leads !== null && leads.length > 0 && (
        <UpNextRail items={upNext} />
      )}

      {leads !== null && leads.length > 0 && (
        <FunnelScoreboard
          counts={statusCounts}
          demosEver={demosEver}
          moved={statusCounts['In Pipeline']}
        />
      )}

      {leads !== null && leads.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, industry, host…"
            className="fp-input sm:max-w-[16rem]"
            aria-label="Search previews"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {LeadStatusEnum.options.map((status) => {
              const on = statusFilter.has(status);
              const meta = STATUS_META[status];
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleStatus(status)}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide transition-colors"
                  style={
                    on
                      ? { borderColor: meta.color, background: meta.bg, color: meta.color }
                      : { borderColor: 'var(--nm-border)', color: 'var(--nm-muted)' }
                  }
                >
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: meta.color, opacity: on ? 1 : 0.45 }}
                  />
                  {status}
                  <span className="opacity-70">{statusCounts[status]}</span>
                </button>
              );
            })}
            {filtering && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter(new Set());
                  setSearch('');
                }}
                className="font-mono text-[10px] uppercase tracking-widest text-nm-accent hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {listError && (
        <div className="rounded-[12px] border border-nm-danger/30 bg-nm-danger-bg px-4 py-3 text-sm text-nm-danger" role="alert">
          <p className="font-medium">Could not load leads from Firestore</p>
          <p className="mt-1 text-xs">{listError}</p>
        </div>
      )}

      {leads === null ? (
        <div className="fp-card text-center font-mono text-sm text-nm-muted">Loading leads…</div>
      ) : grouped.length === 0 ? (
        listError ? (
          <div className="fp-card text-center text-sm text-nm-muted">
            Resolve the Firestore error above, then refresh the page.
          </div>
        ) : filtering ? (
          <div className="fp-card border-dashed text-center text-sm text-nm-muted">
            Nothing matches — clear the search or status pills above.
          </div>
        ) : (
          <div className="fp-card border-dashed py-10 text-center">
            <p className="fp-label">Flashpoint</p>
            <h2 className="mt-2 font-display text-xl font-semibold text-nm-ink">No previews yet</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm text-nm-muted">
              Name + industry is enough to generate a site. Open a call sheet and build it while
              they talk.
            </p>
            <button type="button" onClick={() => setNewOpen(true)} className="fp-btn fp-btn-primary mt-5">
              + New preview
            </button>
          </div>
        )
      ) : (
        <div className="space-y-6">
          {grouped.map(([status, rows]) => {
            const meta = STATUS_META[status];
            return (
              <div key={status} className="space-y-3">
                <div className="flex items-center gap-3 px-1">
                  <p className="fp-label whitespace-nowrap" style={{ color: meta.color }}>
                    {status}
                  </p>
                  <span className="font-mono text-[11px] text-nm-tertiary">{rows.length}</span>
                  <span aria-hidden className="nm-rule-ticked min-w-0 flex-1" />
                  <span className="hidden whitespace-nowrap font-mono text-[10px] uppercase tracking-widest text-nm-tertiary sm:block">
                    {meta.hint}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {rows.map((lead, i) => (
                    <div
                      key={lead.id}
                      className="fp-rise"
                      style={{ animationDelay: `${Math.min(i * 45, 360)}ms` }}
                    >
                      <LeadListRow lead={lead} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NewLeadDialog open={newOpen} onClose={() => setNewOpen(false)} />
    </section>
  );
}

function UpNextRail({ items }: { items: UpNextItem[] }) {
  const shown = items.slice(0, 4);
  const more = items.length - shown.length;

  return (
    <div className="fp-card !py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="fp-label whitespace-nowrap">Up next</p>
        {shown.length === 0 ? (
          <p className="text-sm text-nm-muted">
            Nothing waiting on you — every sheet is generated or moved on.
          </p>
        ) : (
          <>
            {shown.map((item) => {
              const color =
                item.tone === 'ready'
                  ? 'var(--nm-success)'
                  : item.tone === 'gap'
                    ? 'var(--nm-info)'
                    : 'var(--nm-warn)';
              return (
                <Link
                  key={item.leadId}
                  to={`/leads/${item.leadId}`}
                  className="group flex min-w-0 items-center gap-2 rounded-full border border-nm-border px-3 py-1.5 text-sm transition-colors hover:border-nm-accent"
                >
                  <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
                  <span className="truncate font-medium text-nm-ink group-hover:text-nm-accent">
                    {item.name}
                  </span>
                  <span className="whitespace-nowrap text-xs text-nm-muted">{item.note}</span>
                </Link>
              );
            })}
            {more > 0 && <span className="font-mono text-[11px] text-nm-tertiary">+{more} more</span>}
          </>
        )}
      </div>
    </div>
  );
}

function FunnelScoreboard({
  counts,
  demosEver,
  moved,
}: {
  counts: Record<LeadStatus, number>;
  demosEver: number;
  moved: number;
}) {
  return (
    <div className="fp-card overflow-hidden !p-0">
      <div className="grid grid-cols-3 divide-x divide-nm-border">
        {LeadStatusEnum.options.map((status, i) => (
          <FunnelSegment
            key={status}
            status={status}
            count={counts[status]}
            last={i === LeadStatusEnum.options.length - 1}
          />
        ))}
      </div>
      {demosEver > 0 && (
        <div className="border-t border-nm-border px-4 py-2 sm:px-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nm-tertiary">
            {moved} of {demosEver} demo{demosEver === 1 ? '' : 's'} moved to pipeline all-time
          </p>
        </div>
      )}
    </div>
  );
}

function FunnelSegment({
  status,
  count,
  last,
}: {
  status: LeadStatus;
  count: number;
  last: boolean;
}) {
  const meta = STATUS_META[status];
  const value = useCountUp(count);
  return (
    <div className="relative min-w-0 px-4 py-4 sm:px-5">
      <div className="flex items-center gap-1.5">
        <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: meta.color }} />
        <span className="fp-label truncate">{status}</span>
      </div>
      <div className="mt-2 font-display text-4xl font-semibold leading-none" style={{ color: meta.color }}>
        {value}
      </div>
      {!last && (
        <span
          aria-hidden
          className="absolute right-0 top-1/2 z-10 hidden -translate-y-1/2 translate-x-1/2 bg-nm-card px-0.5 font-mono text-xs text-nm-tertiary sm:block"
        >
          →
        </span>
      )}
    </div>
  );
}

/** Ease a number from its previous value to `target` over ~700ms. */
function useCountUp(target: number, duration = 700): number {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    if (from === target) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      fromRef.current = target;
    };
  }, [target, duration]);

  return value;
}
